<template>
	<div>
		<slot
			:checkoutMethod="redirectToBcCheckout"
			:emptyBag="removeAllItems"
			:reduceItemQty="reduceItemQty"
			:increaseItemQty="increaseItemQty"
			:removeItem="removeItem"
			:reduceCertQty="reduceCertQty"
			:increaseCertQty="increaseCertQty"
			:removeCert="removeCert"
		/>
	</div>
</template>

<script>
import Swal from 'sweetalert2'
import { useCheckoutStore } from '../../stores/store.js'
import axios from 'axios'
import helpers from '../../helpers/checkout.js'

export default {
	inject: ['store'],
	props: {
		productId: {
			type: Number,
		},
	},
	data() {
		return {
			checkout: useCheckoutStore(),
			bcCart: {
				customer_id: 0,
				line_items: [],
				channel_id: 1,
				currency: {
					code: 'USD',
				},
				locale: 'en-US',
			},
		}
	},
	methods: {
		addToBag() {
			let existingItem = this.store.cart?.cartItems?.find?.((c) => c.item.id === this.product.id)
			if (existingItem) {
				existingItem.quantity += 1
			} else {
				this.store.cart?.cartItems?.push?.({
					item: this.product,
					quantity: 1,
				})
			}
			Swal.fire({
				icon: 'success',
				title: 'Item added to bag',
				showConfirmButton: false,
				timer: 1500,
			})
		},
		getItemPurchaseIncrement(item) {
			// Required purchase increment: the variant's wins, falling back to the product's, then 1.
			const variantInc = Number(item?.variant?.purchaseIncrement)
			if (Number.isFinite(variantInc) && variantInc > 0) return variantInc
			const productInc = Number(item?.item?.purchaseIncrement)
			if (Number.isFinite(productInc) && productInc > 0) return productInc
			return 1
		},
		reduceItemQty(itemId) {
			let item = this.store.cart?.cartItems?.find((ci) => ci?.variant?.id == itemId)
			if (!item) item = this.store.cart?.cartItems?.find((ci) => ci.item.id == itemId)

			const inc = this.getItemPurchaseIncrement(item)
			if (item.quantity > inc) {
				item.quantity -= inc
			} else {
				this.removeItem(itemId)
			}
			this.checkout.onCheckoutCartChanged()
		},
		increaseItemQty(itemId) {
			let item = this.store.cart?.cartItems?.find((ci) => ci?.variant?.id == itemId)
			if (!item) item = this.store.cart?.cartItems?.find((ci) => ci.item.id == itemId)

			item.quantity += this.getItemPurchaseIncrement(item)
			this.checkout.onCheckoutCartChanged()
		},
		removeItem(itemId) {
			let isVariant = true

			let item = this.store.cart?.cartItems?.find((ci) => ci?.variant?.id == itemId)
			if (!item) {
				item = this.store.cart?.cartItems?.find((ci) => ci.item.id == itemId)
				isVariant = false
			}

			Swal.fire({
				text: 'Are you sure you want to delete this item?',
				icon: 'warning',
				showCancelButton: true,
				confirmButtonColor: '#444',
				cancelButtonColor: 'transparent',
				confirmButtonText: 'OK',
				cancelButtonText: 'Cancel',
				customClass: {
					confirmButton: 'swal-confirm-button',
					cancelButton: 'swal-cancel-button',
				},
				didOpen: () => {
					document.querySelector('.swal-confirm-button').style.width = '100px'
					document.querySelector('.swal-cancel-button').style.color = '#666666'
					document.querySelector('.swal-cancel-button').style.border = 'none'
				},
			}).then((result) => {
				if (result.isConfirmed) {
					if (this.store.cart.cartItems.length === 1) {
						// If last item is being decremented, clear the cart
						this.store.cart.cartItems = []
						this.store.cart.subTotal = 0
						this.store.cart.total = 0
						this.store.cart.tax = 0
						this.store.showBag = false
					} else {
						if (isVariant) {
							this.store.cart.cartItems = this.store.cart.cartItems.filter((i) => i?.variant?.id !== item?.variant?.id)
						} else {
							this.store.cart.cartItems = this.store.cart.cartItems.filter((i) => i?.item?.id !== item?.item?.id)
						}
					}
					this.checkout.onCheckoutCartChanged()
				}
			})
		},
		async removeAllItems() {
			const result = await Swal.fire({
				text: 'Are you sure you want to delete the cart?',
				icon: 'warning',
				showCancelButton: true,
				confirmButtonColor: '#444',
				cancelButtonColor: 'transparent',
				confirmButtonText: 'OK',
				cancelButtonText: 'Cancel',
				customClass: {
					confirmButton: 'swal-confirm-button',
					cancelButton: 'swal-cancel-button',
				},
				didOpen: () => {
					document.querySelector('.swal-confirm-button').style.width = '100px'
					document.querySelector('.swal-cancel-button').style.color = '#666666'
					document.querySelector('.swal-cancel-button').style.border = 'none'
				},
			})

			if (result.isConfirmed) {
				this.store.cart.cartItems = []
				this.store.cart.certificates = []
				this.store.cart.subTotal = 0
				this.store.cart.total = 0
				this.store.cart.tax = 0
				this.store.showBag = false

				this.store.cart.coupon = {}

				if (this.store.cart?.staffNote) delete this.store.cart.staffNote
				this.store.cart.kitsAdded = []
				this.store.cart.kitId = ''

				// Sync snapshot immediately so re-adding the same kit still triggers saveCart. That also leaves the
				// cart watcher with no difference to see, so the empty cart is saved here or it never reaches the server.
				if (typeof this.store.syncOriginalCart === 'function') {
					this.store.syncOriginalCart()
				}

				this.checkout.onCheckoutCartChanged()
				if (typeof this.store.saveCart === 'function') {
					await this.store.saveCart()
				}
			}
		},
		reduceCertQty(certIndex) {
			let cert = this.store.cart?.certificates[certIndex]
			if (cert.quantity > 1) {
				cert.quantity -= 1
			} else {
				this.removeCert(certIndex)
			}
		},
		increaseCertQty(certIndex) {
			let cert = this.store.cart?.certificates[certIndex]
			cert.quantity += 1
		},
		removeCert(certIndex) {
			Swal.fire({
				text: 'Are you sure you want to delete this gift certificate?',
				icon: 'warning',
				showCancelButton: true,
				confirmButtonColor: '#444',
				cancelButtonColor: 'transparent',
				confirmButtonText: 'OK',
				cancelButtonText: 'Cancel',
				customClass: {
					confirmButton: 'swal-confirm-button',
					cancelButton: 'swal-cancel-button',
				},
				didOpen: () => {
					document.querySelector('.swal-confirm-button').style.width = '100px'
					document.querySelector('.swal-cancel-button').style.color = '#666666'
					document.querySelector('.swal-cancel-button').style.border = 'none'
				},
			}).then((result) => {
				if (result.isConfirmed) {
					this.store.cart.certificates.splice(certIndex, 1)
				}
			})
		},
		async redirectToBcCheckout() {
			// Send cart to Mango and redirect to BC checkout

			let bcCart = {
				line_items: [],
				channel_id: 1,
				currency: {
					code: 'USD',
				},
				locale: 'en-US',
			}

			for (let i of this.store.cart.cartItems) {
				bcCart.line_items.push({
					product_id: i.item?.bcId,
					quantity: i.quantity,
					variant_id: i.variant?.bcId,
				})
			}

			let response = await axios.post(`${this.store.api}/controllers/bigCommerce/checkout`, bcCart)

			// The cart itself is stored on the server; this call only builds the hosted BigCommerce checkout.
			window.location.href = response.data.checkoutURL
		},
		async applyCertificate() {
			const url = `${this.store.api}/controllers/bigCommerce/checkout/carts/gift_certificates/${this.certificateCode}`
			const response = await axios.get(url)

			if (!response?.data?.success) {
				return Swal.fire({
					icon: 'error',
					title: 'Invalid Certificate Code',
					text: 'The provided code does not match any valid gift certificate.',
				})
			}

			this.store.cart.certificate = response?.data?.data
			await this.store.saveCart?.()
		},
		async removeCertificate() {
			this.store.cart.certificate = { code: '' }
			await this.store.saveCart?.()
		},
	},
}
</script>

<style scoped>
/* .swal-confirm-button {
    width: 100px !important;
}

.swal-cancel-button {
    color: var(--ihh-gray-700) !important;
    border: none !important;
} */
</style>
